import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useUIStore } from '@/stores/ui.store'
import { useAssociado } from '@/hooks/useAssociados'
import { useContratos } from '@/hooks/useContratos'
import { useVeiculos } from '@/hooks/useVeiculos'
import { useFaturas, useMarcarFaturaPaga, useGerarProRata } from '@/hooks/useFaturas'
import { NovoContratoWizard } from '@/components/admin/NovoContratoWizard'
import {
    Mail, Phone, MapPin, Calendar, Hash,
    FileText, Car, CreditCard, ExternalLink, Plus, Loader2, ArrowLeft,
    RefreshCcw, Search, Edit2, Check, CheckCircle, AlertCircle, Heart
} from 'lucide-react'
import { useAssociadosPorCpf, useUpdateAssociado } from '@/hooks/useAssociados'
import { ContratoDetailDrawer } from '@/components/admin/ContratoDetailDrawer'
import { VeiculoDetailDrawer } from '@/components/admin/VeiculoDetailDrawer'
import { FaturaDetailDrawer } from '@/components/admin/FaturaDetailDrawer'
import { formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import { Filter } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { faturaService, FaturaExpanded } from '@/services/fatura.service'
import { ContratoExpanded } from '@/services/contrato.service'
import { VeiculoComAssociado } from '@/services/veiculo.service'
import { mascararCPF } from '@/lib/validators'
import { usePontosExtrato } from '@/hooks/usePontos'
import { supabase } from '@/config/supabase'

export function AssociadoPerfilPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [activeTab, setActiveTab] = useState<'overview' | 'servicos' | 'veiculos' | 'financeiro' | 'pontos'>('overview')
    const [showSempreJuntosModal, setShowSempreJuntosModal] = useState(false)
    const [showWizard, setShowWizard] = useState(false)
    const [selectedContrato, setSelectedContrato] = useState<ContratoExpanded | null>(null)
    const [selectedVeiculo, setSelectedVeiculo] = useState<VeiculoComAssociado | null>(null)
    const [selectedFatura, setSelectedFatura] = useState<FaturaExpanded | null>(null)

    const proRataMutation = useGerarProRata()
    const marcarPagaMutation = useMarcarFaturaPaga()

    const { data: associado, isLoading: loadingAssoc } = useAssociado(id || null)
    const { data: associadosCpf } = useAssociadosPorCpf(associado?.cpf || null)
    const updateMutation = useUpdateAssociado()

    const [isEditing, setIsEditing] = useState(false)
    const [editForm, setEditForm] = useState({
        nome: '',
        email: '',
        telefone: '',
        cpf: '',
        data_nascimento: '',
        endereco_rua: '',
        endereco_numero: '',
        endereco_bairro: '',
        endereco_cidade: '',
        endereco_uf: '',
        endereco_cep: ''
    })

    useEffect(() => {
        if (associado) {
            setPageTitle(`Perfil: ${associado.nome}`)
            setEditForm({
                nome: associado.nome || '',
                email: associado.email || '',
                telefone: associado.telefone || '',
                cpf: associado.cpf || '',
                data_nascimento: associado.data_nascimento || '',
                endereco_rua: associado.endereco?.rua || '',
                endereco_numero: associado.endereco?.numero || '',
                endereco_bairro: associado.endereco?.bairro || '',
                endereco_cidade: associado.endereco?.cidade || '',
                endereco_uf: associado.endereco?.uf || '',
                endereco_cep: associado.endereco?.cep || ''
            })
        }
    }, [associado, setPageTitle])

    const handleSave = async () => {
        if (!associado) return
        try {
            await updateMutation.mutateAsync({
                id: associado.id,
                nome: editForm.nome,
                email: editForm.email,
                telefone: editForm.telefone,
                data_nascimento: editForm.data_nascimento,
                endereco: {
                    rua: editForm.endereco_rua,
                    numero: editForm.endereco_numero,
                    bairro: editForm.endereco_bairro,
                    cidade: editForm.endereco_cidade,
                    uf: editForm.endereco_uf,
                    cep: editForm.endereco_cep
                }
            })
            setIsEditing(false)
            alert('Dados atualizados com sucesso!')
        } catch (err) {
            alert('Erro ao atualizar dados.')
        }
    }

    if (loadingAssoc) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-primary-600" size={32} />
            </div>
        )
    }

    if (!associado) {
        return (
            <div className="p-8 text-center text-surface-700/60 font-medium">
                Associado não encontrado.
            </div>
        )
    }

    const a = associado

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header / Breadcrumb */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin/associados')} className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all text-surface-700">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-surface-900">{a.nome}</h2>
                        <div className="flex items-center gap-2 mt-1">
                            <span className={`badge ${getStatusBadgeClass(a.status)}`}>{translateStatus(a.status)}</span>
                            <span className="text-xs text-surface-700/60 font-medium">{mascararCPF(a.cpf)}</span>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setIsEditing(!isEditing)} className={`btn gap-2 ${isEditing ? 'btn-secondary' : 'btn-ghost text-primary-600 bg-primary-50 hover:bg-primary-100'}`}>
                        <Edit2 size={18} /> {isEditing ? 'Cancelar Edição' : 'Editar Dados'}
                    </button>
                    <button onClick={() => setShowWizard(true)} className="btn btn-primary btn-md gap-2">
                        <Plus size={18} /> Novo Contrato
                    </button>
                </div>
            </div>

            {/* Layout com Sidebar Info e Tabs Content */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                {/* Lateral: Resumo */}
                <aside className="lg:col-span-4 space-y-6">
                    <div className="bg-white rounded-3xl p-6 shadow-elevated">
                        <p className="text-xs font-bold text-surface-700/40 uppercase tracking-widest mb-6">Informações de Contato</p>
                        <div className="space-y-4">
                            <InfoBox icon={Mail} label="E-mail" value={a.email || '—'} />
                            <InfoBox icon={Phone} label="Telefone" value={a.telefone || '—'} />
                            <InfoBox icon={Calendar} label="Data de Nascimento" value={a.data_nascimento ? formatDate(a.data_nascimento) : '—'} />
                            <InfoBox icon={MapPin} label="Endereço"
                                value={a.endereco ? `${a.endereco.rua}, ${a.endereco.numero} - ${a.endereco.bairro}` : '—'}
                            />
                            <InfoBox icon={MapPin} label="Localidade"
                                value={a.endereco ? `${a.endereco.cidade}/${a.endereco.uf} - ${a.endereco.cep}` : '—'}
                            />
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl p-6 shadow-elevated">
                        <p className="text-xs font-bold text-surface-700/40 uppercase tracking-widest mb-4">Registro do Sistema</p>
                        <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                                <span className="text-surface-700/60">ID Associado</span>
                                <span className="font-mono text-xs">{a.id.split('-')[0]}...</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-surface-700/60">Data de Cadastro</span>
                                <span className="font-medium">{formatDate(a.created_at)}</span>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Conteúdo Principal: Abas */}
                <main className="lg:col-span-8 space-y-6">
                    <div className="bg-white rounded-3xl shadow-elevated flex flex-col min-h-[500px] overflow-hidden">
                        <div className="flex border-b border-surface-100 overflow-x-auto no-scrollbar scroll-smooth px-6 pt-2 snap-x snap-mandatory">
                            <TabItem active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={Hash} label="Visão Geral" />
                            <TabItem active={activeTab === 'servicos'} onClick={() => setActiveTab('servicos')} icon={FileText} label="Contratos" />
                            <TabItem active={activeTab === 'veiculos'} onClick={() => setActiveTab('veiculos')} icon={Car} label="Veículos" />
                            <TabItem active={activeTab === 'financeiro'} onClick={() => setActiveTab('financeiro')} icon={CreditCard} label="Financeiro" />
                            <TabItem active={activeTab === 'pontos'} onClick={() => setActiveTab('pontos')} icon={Heart} label="Fidelidade" />
                        </div>

                        <div className="p-8 flex-1">
                            {activeTab === 'overview' && (
                                <div className="animate-fade-in space-y-8">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <OverviewStat label="Status da Conta" value={translateStatus(a.status)} color={a.status === 'ativo' ? 'success' : 'danger'} />
                                        <OverviewStat label="Tempo de Casa" value={`${new Date().getFullYear() - new Date(a.created_at).getFullYear()} anos`} color="primary" />
                                    </div>

                                    {/* Unificação por CPF */}
                                    {associadosCpf && associadosCpf.length > 1 && (
                                        <div className="p-6 rounded-3xl bg-amber-50 border border-amber-100 space-y-4">
                                            <div className="flex items-center gap-2 text-amber-800">
                                                <AlertCircle size={20} />
                                                <h4 className="font-bold">Múltiplos cadastros encontrados para este CPF</h4>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {associadosCpf.map(record => (
                                                    <div key={record.id} className="p-3 rounded-xl bg-white border border-amber-200 text-xs">
                                                        <p className="font-bold text-surface-900 mb-1">{record.nome}</p>
                                                        <div className="flex items-center gap-2 text-surface-600">
                                                            <Mail size={12} /> {record.email || '—'}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-surface-600 mt-1">
                                                            <Phone size={12} /> {record.telefone || '—'}
                                                            {a.metadata?.valid_phone === record.telefone && (
                                                                <span className="flex items-center gap-0.5 text-success-600 font-bold px-1.5 py-0.5 rounded bg-success-50">
                                                                    <Check size={10} /> Válido
                                                                </span>
                                                            )}
                                                            {!isEditing && a.metadata?.valid_phone !== record.telefone && (
                                                                <button 
                                                                    onClick={() => updateMutation.mutate({ id: a.id, metadata: { ...a.metadata, valid_phone: record.telefone } })}
                                                                    className="text-primary-600 hover:underline ml-auto"
                                                                >
                                                                    Marcar como válido
                                                                </button>
                                                            )}
                                                        </div>
                                                        <p className="text-[9px] text-surface-400 mt-2 uppercase">Origem: {record.asaas_empresa || 'Registro Local'}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {isEditing ? (
                                        <div className="animate-fade-in p-8 rounded-3xl bg-surface-50 border border-surface-100 space-y-6">
                                            <h4 className="text-lg font-bold text-surface-900 border-b border-surface-200 pb-4">Editar Informações</h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-surface-700/60 uppercase">Nome Completo</label>
                                                    <input type="text" value={editForm.nome} onChange={e => setEditForm({...editForm, nome: e.target.value})} className="input w-full" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-surface-700/60 uppercase">Email</label>
                                                    <input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="input w-full" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-surface-700/60 uppercase">Telefone</label>
                                                    <input type="text" value={editForm.telefone} onChange={e => setEditForm({...editForm, telefone: e.target.value})} className="input w-full" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-surface-700/60 uppercase">Data de Nascimento</label>
                                                    <input type="date" value={editForm.data_nascimento} onChange={e => setEditForm({...editForm, data_nascimento: e.target.value})} className="input w-full" />
                                                </div>
                                                <div className="sm:col-span-2 grid grid-cols-3 gap-4">
                                                    <div className="col-span-2 space-y-1">
                                                        <label className="text-[10px] font-bold text-surface-700/60 uppercase">Rua</label>
                                                        <input type="text" value={editForm.endereco_rua} onChange={e => setEditForm({...editForm, endereco_rua: e.target.value})} className="input w-full" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-bold text-surface-700/60 uppercase">Nº</label>
                                                        <input type="text" value={editForm.endereco_numero} onChange={e => setEditForm({...editForm, endereco_numero: e.target.value})} className="input w-full" />
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-surface-700/60 uppercase">Bairro</label>
                                                    <input type="text" value={editForm.endereco_bairro} onChange={e => setEditForm({...editForm, endereco_bairro: e.target.value})} className="input w-full" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-surface-700/60 uppercase">CEP</label>
                                                    <input type="text" value={editForm.endereco_cep} onChange={e => setEditForm({...editForm, endereco_cep: e.target.value})} className="input w-full" />
                                                </div>
                                            </div>
                                            <div className="flex justify-end gap-3 pt-4 border-t border-surface-200">
                                                <button onClick={() => setIsEditing(false)} className="btn btn-ghost">Cancelar</button>
                                                <button onClick={handleSave} className="btn btn-primary px-8" disabled={updateMutation.isPending}>
                                                    {updateMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <h4 className="text-lg font-bold text-surface-900 mb-4">Sobre o Associado</h4>
                                            <p className="text-surface-700 leading-relaxed">
                                                {a.nome} é associado desde {formatDate(a.created_at)}. {a.status === 'ativo' ? 'Atualmente mantém sua associação ativa e em dia.' : 'Sua associação encontra-se restrita no momento.'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'servicos' && <ServicosContent associadoId={a.id} onHire={() => setShowWizard(true)} onSelect={setSelectedContrato} />}
                            {activeTab === 'veiculos' && <VeiculosContent associadoId={a.id} onSelect={setSelectedVeiculo} />}
                            {activeTab === 'financeiro' && <FinanceiroContent associadoId={a.id} onSelect={setSelectedFatura} />}
                            {activeTab === 'pontos' && <PontosContent associadoId={a.id} />}
                        </div>
                    </div>
                </main>
            </div>

            <ContratoDetailDrawer contrato={selectedContrato} onClose={() => setSelectedContrato(null)} />
            <VeiculoDetailDrawer veiculo={selectedVeiculo} onClose={() => setSelectedVeiculo(null)} />
            <FaturaDetailDrawer
                fatura={selectedFatura}
                onClose={() => setSelectedFatura(null)}
                onMarcarPaga={(id) => marcarPagaMutation.mutate(id)}
                onGerarProRata={(id) => proRataMutation.mutate(id)}
            />

            {showWizard && (
                <NovoContratoWizard
                    initialAssociado={a}
                    onClose={() => setShowWizard(false)}
                />
            )}
        </div>
    )
}

/* ── Subcomponentes ────────────────────────────────────── */

function InfoBox({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
    return (
        <div className="flex items-start gap-3">
            <div className="mt-1 p-2 rounded-lg bg-surface-50 text-surface-700/40">
                <Icon size={16} />
            </div>
            <div>
                <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">{label}</p>
                <p className="text-sm font-medium text-surface-900 mt-0.5">{value}</p>
            </div>
        </div>
    )
}

function TabItem({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: any; label: string }) {
    return (
        <button onClick={onClick} className={`flex items-center gap-2 px-4 py-4 text-sm font-bold border-b-2 transition-all
            ${active ? 'border-primary-600 text-primary-600' : 'border-transparent text-surface-700/40 hover:text-surface-900'}`}>
            <Icon size={18} /> {label}
        </button>
    )
}

function OverviewStat({ label, value, color }: { label: string; value: string; color: 'success' | 'danger' | 'primary' }) {
    const colors = {
        success: 'text-green-600 bg-green-50 border-green-100',
        danger: 'text-red-600 bg-red-50 border-red-100',
        primary: 'text-primary-600 bg-primary-50 border-primary-100',
    }
    return (
        <div className={`p-4 rounded-2xl border ${colors[color]}`}>
            <p className="text-xs font-bold uppercase tracking-widest opacity-60 mb-1">{label}</p>
            <p className="text-xl font-bold">{value}</p>
        </div>
    )
}

function ServicosContent({ associadoId, onHire, onSelect }: { associadoId: string; onHire: () => void; onSelect: (c: any) => void }) {
    const { data: contratos, isLoading } = useContratos({ associado_id: associadoId })

    if (isLoading) return <Loader2 className="animate-spin mx-auto my-20 text-primary-600" />

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-surface-900">Contratos e Serviços</h4>
                <button onClick={onHire} className="btn btn-secondary btn-sm gap-2">
                    <Plus size={14} /> Novo Serviço
                </button>
            </div>

            {contratos?.data?.length === 0 ? (
                <div className="p-12 text-center border-2 border-dashed border-surface-100 rounded-3xl">
                    <p className="text-surface-700/60 font-medium">Nenhum contrato ativo para este associado.</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {contratos?.data?.map(c => (
                        <div key={c.id}
                            onClick={() => onSelect(c)}
                            className="p-6 rounded-3xl border border-surface-100 bg-white hover:border-primary-100 transition-colors cursor-pointer group">
                            <div className="flex items-center justify-between mb-4">
                                <span className={`badge ${getStatusBadgeClass(c.status)}`}>{translateStatus(c.status)}</span>
                                <span className="text-xs font-bold text-surface-700/40 uppercase tracking-widest">Contrato #{c.id.split('-')[0]}</span>
                            </div>
                            <div className="space-y-3">
                                {c.servicos?.map(s => (
                                    <div key={s.id} className="flex items-center justify-between">
                                        <p className="text-sm font-semibold text-surface-900">{s.servico?.nome}</p>
                                        <p className="text-sm font-bold text-primary-600">R$ {s.valor.toFixed(2)}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-surface-50 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-xs text-surface-700/60 font-medium">
                                    <Car size={14} /> {c.veiculo ? `${c.veiculo.placa} • ${c.veiculo.marca}` : 'Vínculo CPF'}
                                </div>
                                <p className="text-lg font-bold text-surface-900">R$ {c.valor_total_mensal.toFixed(2)}<span className="text-xs font-normal text-surface-700/40">/mês</span></p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

function VeiculosContent({ associadoId, onSelect }: { associadoId: string; onSelect: (v: any) => void }) {
    const { data: veiculos, isLoading } = useVeiculos({ associado_id: associadoId })

    if (isLoading) return <Loader2 className="animate-spin mx-auto my-20 text-primary-600" />

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-surface-900">Veículos do Associado</h4>
                <button className="btn btn-secondary btn-sm gap-2"><Plus size={14} /> Cadastrar Veículo</button>
            </div>

            {veiculos?.data?.length === 0 ? (
                <div className="p-12 text-center border-2 border-dashed border-surface-100 rounded-3xl">
                    <p className="text-surface-700/60 font-medium">Nenhum veículo cadastrado.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {veiculos?.data?.map(v => (
                        <div key={v.id}
                            onClick={() => onSelect(v)}
                            className="p-5 rounded-3xl border border-surface-100 bg-surface-50/30 group hover:border-primary-100 transition-all cursor-pointer">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600">
                                    <Car size={24} />
                                </div>
                                <div>
                                    <p className="text-lg font-mono font-bold text-surface-900 uppercase tracking-tighter">{v.placa}</p>
                                    <p className="text-xs font-bold text-surface-700/40 uppercase tracking-widest">{v.marca} {v.modelo}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs font-medium text-surface-700/60">
                                <span>{v.cor || 'Preto'}</span>
                                <span>•</span>
                                <span>{v.ano || '2023'}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

function FinanceiroContent({ associadoId, onSelect }: { associadoId: string; onSelect: (f: any) => void }) {
    const [statusFiltro, setStatusFiltro] = useState<string>('todos')
    const [placaFiltro, setPlacaFiltro] = useState<string>('')
    const { data: faturas, isLoading } = useFaturas({ associado_id: associadoId, pageSize: 100 })
    const consolidarMutation = useMutation({
        mutationFn: () => faturaService.consolidarServicos(associadoId),
        onSuccess: () => { alert('Faturamento consolidado gerado com sucesso!') }
    })

    if (isLoading) return <Loader2 className="animate-spin mx-auto my-20 text-primary-600" />

    const statusFatura = (f: any) => {
        const hoje = new Date().toISOString().split('T')[0] || ''
        if (f.status === 'pendente' && f.data_vencimento < hoje) return 'atrasada'
        return f.status
    }

    const faturasFiltered = (faturas?.data || []).filter(f => {
        const st = statusFatura(f)
        if (statusFiltro !== 'todos' && st !== statusFiltro) return false
        if (placaFiltro) {
            const placa = (f as any).metadata?._placa_extraida || ''
            if (!placa.toUpperCase().includes(placaFiltro.toUpperCase())) return false
        }
        return true
    })

    return (
        <div className="animate-fade-in space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-surface-900">Histórico Financeiro</h4>
                <button
                    onClick={() => consolidarMutation.mutate()}
                    className="btn btn-ghost btn-sm gap-2 text-primary-600 hover:bg-primary-50"
                >
                    <RefreshCcw size={14} className={consolidarMutation.isPending ? 'animate-spin' : ''} />
                    {consolidarMutation.isPending ? 'Consolidando...' : 'Consolidar Recorrente'}
                </button>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap gap-2">
                <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <input
                        type="text" value={placaFiltro}
                        onChange={e => setPlacaFiltro(e.target.value)}
                        placeholder="Filtrar por placa..."
                        className="input input-sm pl-8 w-36 text-sm"
                    />
                </div>
                <div className="flex items-center gap-1">
                    <Filter size={14} className="text-surface-700/40" />
                    <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)} className="input input-sm w-auto text-sm">
                        <option value="todos">Todos</option>
                        <option value="paga">Paga</option>
                        <option value="pendente">Pendente</option>
                        <option value="atrasada">Atrasada</option>
                        <option value="cancelada">Cancelada</option>
                    </select>
                </div>
            </div>

            {faturasFiltered.length === 0 ? (
                <div className="p-12 text-center border-2 border-dashed border-surface-100 rounded-3xl">
                    <p className="text-surface-700/60 font-medium">Nenhuma fatura encontrada.</p>
                </div>
            ) : (
                <div className="bg-white rounded-3xl border border-surface-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-surface-50/50 border-b border-surface-100">
                                <th className="px-4 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Valor</th>
                                <th className="px-4 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Vencimento</th>
                                <th className="px-4 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Status</th>
                                <th className="px-4 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Placa</th>
                                <th className="px-4 py-4 text-right"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-surface-50">
                            {faturasFiltered.map(f => {
                                const st = statusFatura(f)
                                const placa = (f as any).metadata?._placa_extraida
                                return (
                                    <tr key={f.id}
                                        onClick={() => onSelect(f)}
                                        className="hover:bg-surface-50/30 transition-colors cursor-pointer">
                                        <td className="px-4 py-4">
                                            <p className="text-sm font-bold text-surface-900">R$ {f.valor.toFixed(2)}</p>
                                            <p className="text-[10px] uppercase font-bold text-surface-400">
                                                {f.tipo === 'mensalidade' ? 'Mensalidade' : f.tipo === 'adesao' ? 'Taxa de Adesão' : f.tipo}
                                            </p>
                                        </td>
                                        <td className="px-4 py-4">
                                            <p className="text-sm text-surface-700 font-medium">{formatDate(f.data_vencimento)}</p>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`badge ${getStatusBadgeClass(st)}`}>{translateStatus(st)}</span>
                                        </td>
                                        <td className="px-4 py-4">
                                            {placa ? (
                                                <span className="font-mono text-xs font-bold text-surface-900 bg-surface-100 px-2 py-1 rounded-lg">{placa}</span>
                                            ) : (
                                                <span className="text-surface-700/30 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-right">
                                            <button className="p-2 rounded-xl hover:bg-white hover:shadow-sm text-surface-700 transition-all">
                                                <ExternalLink size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

function PontosContent({ associadoId }: { associadoId: string }) {
    const { data: extrato, isLoading, refetch } = usePontosExtrato(associadoId)
    const [adding, setAdding] = useState(false)
    const [novoPonto, setNovoPonto] = useState({ pontos: 10, descricao: 'Ajuste Manual' })

    const addMutation = useMutation({
        mutationFn: async () => {
            const { data: assoc } = await supabase.schema('veicular_system').from('veic_associados').select('tenant_id').eq('id', associadoId).single()
            if (!assoc) throw new Error('Associado não encontrado')
            
            const { error } = await supabase.schema('veicular_system').from('veic_pontos_extrato').insert({
                tenant_id: assoc.tenant_id,
                associado_id: associadoId,
                pontos: novoPonto.pontos,
                descricao: novoPonto.descricao,
                tipo: 'manual'
            })
            if (error) throw error
        },
        onSuccess: () => {
            setAdding(false)
            refetch()
            alert('Pontos adicionados com sucesso!')
        }
    })

    if (isLoading) return <Loader2 className="animate-spin mx-auto my-20 text-primary-600" />

    const saldo = extrato?.reduce((acc, curr) => acc + curr.pontos, 0) || 0

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h4 className="text-lg font-bold text-surface-900">Programa Sempre Juntos</h4>
                    <p className="text-xs text-surface-700/60 font-bold uppercase tracking-widest mt-0.5">Saldo: {saldo} pts</p>
                </div>
                <button onClick={() => setAdding(true)} className="btn btn-secondary btn-sm gap-2">
                    <Plus size={14} /> Atribuir Pontos
                </button>
            </div>

            {adding && (
                <div className="p-4 rounded-2xl bg-surface-50 border border-surface-100 flex flex-wrap gap-3 items-end">
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-surface-700/60 uppercase">Quantidade</label>
                        <input type="number" value={novoPonto.pontos} onChange={e => setNovoPonto({...novoPonto, pontos: parseInt(e.target.value)})} className="input input-sm w-24" />
                    </div>
                    <div className="flex-1 space-y-1">
                        <label className="text-[10px] font-bold text-surface-700/60 uppercase">Descrição</label>
                        <input type="text" value={novoPonto.descricao} onChange={e => setNovoPonto({...novoPonto, descricao: e.target.value})} className="input input-sm" />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setAdding(false)} className="btn btn-ghost btn-sm">Cancelar</button>
                        <button onClick={() => addMutation.mutate()} className="btn btn-primary btn-sm" disabled={addMutation.isPending}>
                            {addMutation.isPending ? 'Gravando...' : 'Confirmar'}
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-3xl border border-surface-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead>
                        <tr className="bg-surface-50/50 border-b border-surface-100">
                            <th className="px-4 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Data</th>
                            <th className="px-4 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest">Descrição</th>
                            <th className="px-4 py-4 text-[10px] font-bold text-surface-700/40 uppercase tracking-widest text-right">Pontos</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-50">
                        {extrato?.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="px-4 py-10 text-center text-surface-700/40 text-sm font-medium">Nenhum registro de pontos.</td>
                            </tr>
                        ) : (
                            extrato?.map(e => (
                                <tr key={e.id}>
                                    <td className="px-4 py-3 text-sm text-surface-700">{formatDate(e.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <p className="text-sm font-semibold text-surface-900">{e.descricao}</p>
                                        <p className="text-[10px] uppercase font-bold text-surface-400">{e.tipo}</p>
                                    </td>
                                    <td className={`px-4 py-3 text-sm font-bold text-right ${e.pontos > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {e.pontos > 0 ? '+' : ''}{e.pontos}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
